import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiServices from "../Services/ApiServices";
import { toastHelper } from "../../Helpers/toastHelper";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faArrowLeft,
  faPaperPlane,
  faSpinner,
  faShieldAlt,
  faKey
} from "@fortawesome/free-solid-svg-icons";
import extractErrorMessage from "../../Helpers/APIHelper";
import { AppKeys } from "../Shared/AppKeys";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "" });
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle form submission
  const handleRequestResetLink = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const frontendUrl = AppKeys._FRONTEND_URL;

      await ApiServices.post("User/ForgetPassword", {
        email: formData.email,
        url: frontendUrl
      });

      toastHelper.success("تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني");
      navigate("/signin");
    } catch (err) {
      const title = err?.title || err?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        const errMessage = extractErrorMessage(err);
        toastHelper.error(errMessage);
      } else {
        toastHelper.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-page">
      <div className="background-animation">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      <motion.div
        className="form-container"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <button className="back-btn" onClick={() => navigate("/signin")}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>

        <div className="icon-badge">
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          >
            <FontAwesomeIcon icon={faShieldAlt} className="main-icon" />
          </motion.div>
          <div className="badge-notification">
            <FontAwesomeIcon icon={faKey} />
          </div>
        </div>

        <div className="text-content">
          <h1>نسيت كلمة المرور؟</h1>
          <p>لا تقلق! أدخل بريدك الإلكتروني وسنرسل لك تعليمات لاستعادة حسابك.</p>
        </div>

        <form onSubmit={handleRequestResetLink}>
          <div className="input-group">
            <div className={`input-field ${isFocused ? 'focused' : ''}`}>
              <FontAwesomeIcon icon={faEnvelope} className="field-icon" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder="البريد الإلكتروني"
                required
              />
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <>
                <span>إرسال الرابط</span>
                <FontAwesomeIcon icon={faPaperPlane} />
              </>
            )}
          </button>
        </form>

        <div className="footer-link">
          تذكرت كلمة المرور؟ <span onClick={() => navigate("/signin")}>تسجيل الدخول</span>
        </div>
      </motion.div>

      <style jsx>{`
        .forgot-password-page {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f8fafc;
            position: relative;
            overflow: hidden;
              font-family: "Zain", sans-serif;
            direction: rtl;
            padding: 20px;
        }

        .background-animation {
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            z-index: 1;
        }

        .shape {
            position: absolute;
            border-radius: 50%;
            filter: blur(80px);
            opacity: 0.5;
            animation: float 20s infinite;
        }

        .shape-1 { width: 400px; height: 400px; background: #667eea; top: -100px; right: -100px; }
        .shape-2 { width: 300px; height: 300px; background: #764ba2; bottom: -50px; left: -50px; animation-delay: -5s; }
        .shape-3 { width: 200px; height: 200px; background: #f093fb; top: 40%; left: 40%; animation-delay: -10s; }

        @keyframes float {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            33% { transform: translate(30px, -50px) rotate(10deg); }
            66% { transform: translate(-20px, 20px) rotate(-5deg); }
        }

        .form-container {
            width: 100%;
            max-width: 480px;
            background: rgba(255,255,255,0.9);
            backdrop-filter: blur(20px);
            padding: 40px;
            border-radius: 24px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.08);
            border: 1px solid rgba(255,255,255,0.8);
            position: relative;
            z-index: 10;
            text-align: center;
        }

        .back-btn {
            position: absolute;
            top: 24px;
            left: 24px;
            background: #f1f5f9;
            border: none;
            width: 40px;
            height: 40px;
            border-radius: 12px;
            color: #64748b;
            cursor: pointer;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .back-btn:hover {
            background: #e2e8f0;
            color: #1e293b;
            transform: translateX(2px);
        }

        .icon-badge {
            width: 80px;
            height: 80px;
            background: linear-gradient(135deg, #e0e7ff 0%, #f3e8ff 100%);
            border-radius: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 30px;
            position: relative;
        }

        .main-icon {
            font-size: 2.5rem;
            color: #6366f1;
        }

        .badge-notification {
            position: absolute;
            bottom: -5px;
            right: -5px;
            background: #ffffff;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 10px rgba(0,0,0,0.1);
            color: #6366f1;
            font-size: 0.9rem;
        }

        .text-content h1 {
            font-size: 1.8rem;
            font-weight: 800;
            color: #1e293b;
            margin-bottom: 12px;
        }

        .text-content p {
            color: #64748b;
            font-size: 1rem;
            line-height: 1.6;
            margin-bottom: 32px;
            max-width: 90%;
            margin-left: auto;
            margin-right: auto;
        }

        .input-group {
            text-align: right;
            margin-bottom: 24px;
        }

        .input-group label {
            display: block;
            font-weight: 700;
            font-size: 0.9rem;
            color: #334155;
            margin-bottom: 8px;
        }

        .input-field {
            display: flex;
            align-items: center;
            background: #ffffff;
            border: 1px solid #e2e8f0;
            padding: 0 16px;
            transition: all 0.3s ease;
            box-shadow: 0 2px 4px rgba(148, 163, 184, 0.05);
            transform: translateZ(0);
            width: 100%;
            box-sizing: border-box;
            overflow: hidden;
        }

        .input-field.focused {
            border-color: #6366f1;
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
            transform: translateY(-1px);
        }

        .field-icon {
            color: #94a3b8;
            font-size: 1.1rem;
            transition: 0.3s;
        }
        
        .input-field.focused .field-icon {
            color: #6366f1;
        }

        .input-field input {
            width: 100%;
            border: none;
            background: transparent;
            padding: 16px 12px;
            font-size: 1rem;
            color: #1e293b;
            outline: none;
            font-family: inherit;
        }

        .input-field input::placeholder {
            color: #cbd5e1;
        }

        /* Autofill Fix */
        .input-field input:-webkit-autofill,
        .input-field input:-webkit-autofill:hover, 
        .input-field input:-webkit-autofill:focus, 
        .input-field input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 30px #ffffff inset !important;
            -webkit-text-fill-color: #1e293b !important;
            transition: background-color 5000s ease-in-out 0s;
        }

        .submit-btn {
            width: 100%;
            padding: 16px;
            background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
            color: white;
            border: none;
            border-radius: 16px;
            font-weight: 700;
            font-size: 1.1rem;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            transition: all 0.3s ease;
            box-shadow: 0 10px 20px rgba(99, 102, 241, 0.2);
            font-family: inherit;
        }

        .submit-btn:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 15px 30px rgba(99, 102, 241, 0.3);
        }

        .submit-btn:disabled {
            opacity: 0.7;
            cursor: wait;
        }

        .footer-link {
            margin-top: 32px;
            color: #64748b;
            font-size: 0.95rem;
        }

        .footer-link span {
            color: #6366f1;
            font-weight: 700;
            cursor: pointer;
            margin-right: 5px;
            transition: color 0.2s;
        }

        .footer-link span:hover {
            color: #4f46e5;
            text-decoration: underline;
        }

        @media(max-width: 480px) {
            .forgot-password-page {
                padding: 16px;
            }
            .form-container {
                padding: 30px 20px;
            }
            .text-content h1 {
                font-size: 1.5rem;
            }
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;